import { z } from "zod";
import type { MessageKey } from "../i18n/messages";
import { makeNameSchema, makePhoneSchema, PREFERRED_LANGUAGE_VALUES } from "./enrollmentSchema";
import { US_STATES } from "./states";

type Translator = (key: MessageKey) => string;

const ZIP_RE = /^\d{5}(-\d{4})?$/;
const EMAIL_RE = /\S+@\S+\.\S+/;
const STATE_CODE_TUPLE = US_STATES.map((s) => s.code) as [string, ...string[]];

/**
 * The member portal's add-caregiver form, mirroring the server rules of
 * `PortalCaregiverPut` (server/app/models/api/portal.py): non-blank names and
 * relationship, a required email, a phone that reduces to 10 US digits (sent
 * as typed; the server normalises it), one of `PREFERRED_LANGUAGE_VALUES`,
 * a street address with a USPS state code and a 5 or 9 digit ZIP, and the
 * consent box checked. The caregiver address has no PO-box rule.
 */
export function makeCaregiverInviteSchema(t: Translator) {
  return z.object({
    firstName: makeNameSchema(t),
    lastName: makeNameSchema(t),
    relationship: makeNameSchema(t),
    email: z.string().trim().min(1, t("err.required")).regex(EMAIL_RE, t("err.email")),
    phone: makePhoneSchema(t),
    preferredLanguage: z.enum(PREFERRED_LANGUAGE_VALUES),
    address: z.object({
      street1: z.string().trim().min(1, t("err.required")),
      street2: z.string().trim(),
      city: z.string().trim().min(1, t("err.required")),
      state: z
        .string()
        .min(1, t("err.selectState"))
        .pipe(z.enum(STATE_CODE_TUPLE, { message: t("err.selectState") })),
      zip: z.string().trim().regex(ZIP_RE, t("err.zip")),
    }),
    consent: z.literal(true, { message: t("err.required") }),
  });
}

const _typeProbe = makeCaregiverInviteSchema((k) => k);
/** The form's field values before parsing (`consent` is any boolean while typing). */
export type CaregiverInviteFields = Omit<z.input<typeof _typeProbe>, "consent"> & {
  consent: boolean;
};
/** The parsed values: trimmed strings and `consent: true`. */
export type CaregiverInviteValues = z.output<typeof _typeProbe>;

/** The empty form, with the language at its server default. */
export const caregiverInviteDefaults: CaregiverInviteFields = {
  firstName: "",
  lastName: "",
  relationship: "",
  email: "",
  phone: "",
  preferredLanguage: "english",
  address: { street1: "", street2: "", city: "", state: "", zip: "" },
  consent: false,
};
